import { Cancel, ContentCopy, DeleteOutline, Edit, Info, OpenInNewOutlined, Save } from "@mui/icons-material";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridEditInputCell,
  GridEditSingleSelectCell,
  GridRowModes,
  type GridEditInputCellProps,
  type GridEditSingleSelectCellProps,
  type GridRowModesModel,
} from "@mui/x-data-grid";
import { GridPreProcessEditCellProps, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { OptionsObject, SnackbarKey, SnackbarMessage } from "notistack";
import React from "react";
import { AttachmentImage } from "../../components";
import { PublishedIcon } from "../../images/svg/publishedIcon";
import { WIPIcon } from "../../images/svg/WIPIcon";
import { SelectBoxOption } from "../../interfaces";
import { AppRoutes } from "../../router/routes";

export const calculateMaterialTotalCost = (row: any) => {
  let normal_quantity = row?.normal_quantity ?? 0;
  let cost = row?.cost ?? 0;
  if ([true, "Yes"].includes(row.spare_part)) {
    let spare_part_quantity = row?.spare_part_quantity ?? 0;
    normal_quantity = row.spare_part_quantity
      ? spare_part_quantity + normal_quantity
      : normal_quantity;
  }
  if ([true, "Yes"].includes(row.cost_override)) {
    let cost_per_qty = row?.cost_per_qty ?? 0;
    return row.cost_per_qty ? cost_per_qty * normal_quantity : 0;
  } else {
    return normal_quantity * cost;
  }
};

export const enableSpareQtyBasedOnSparePart: React.FC<GridEditInputCellProps> = (
  params
) => {
  return (
    <GridEditInputCell
      {...params}
      disabled={![true, "Yes"].includes(params?.row?.spare_part)}
    />
  );
};

const enableCategoryBasedOnSparePart: React.FC<
  GridEditSingleSelectCellProps | GridEditInputCellProps
> = (params, sparePartsData) => {
  if (![true, "Yes"].includes(params?.row?.spare_part)) {
    return (
      <GridEditInputCell
        {...(params as GridEditInputCellProps)}
        disabled={![true, "Yes"].includes(params?.row?.spare_part)}
        value={
          sparePartsData.data?.spareParts?.find(
            (s: SelectBoxOption) => s?._id === params?.row?.spare_part_category
          )?.label ?? ""
        }
      />
    );
  }
  return (
    <GridEditSingleSelectCell
      {...(params as GridEditSingleSelectCellProps)}
      disabled={![true, "Yes"].includes(params?.row?.spare_part)}
    />
  );
};

export const Columns = (
  type: "bom" | "material",
  handleSaveClick: any,
  handleEditClick: any,
  handleCancelClick: any,
  handleDeleteClick: any,
  rowModesModel: GridRowModesModel,
  isEditable: boolean = false,
  sparePartsData: any,
  bomStatusData: any,
  materialStatusData: any,
  defaultCurrency: string,
  paths: any[],
  cloneElement?: (row: any) => void,
  enqueueSnackbar?: (message: SnackbarMessage, options?: OptionsObject | undefined) => SnackbarKey
): GridColDef[] => {
  let rows: GridColDef[] = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const isInEditMode =
          rowModesModel?.[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Save />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<Cancel />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Edit fontSize="small" />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          cloneElement ? <GridActionsCellItem
            icon={<ContentCopy fontSize="small" />}
            label="Edit"
            className="textPrimary"
            onClick={() => cloneElement(row)}
            color="inherit"
          /> : <></>,
          <GridActionsCellItem
            icon={<DeleteOutline fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(row.child_id)}
            color="inherit"
          />,
        ];
      },
    },
    {
      field: 'material-bom-detail',
      headerName: type === "bom" ? "BOM" : "Material",
      width: 340,
      valueGetter: (value, row) => type === "bom" ? row.bom.sequence_id : row.material.sequence_id,
      renderCell: (params: GridRenderCellParams) => {
        return <Stack direction={"row"} sx={{
          alignItems: "center",
          gap: 1
        }}>
          <AttachmentImage object_id={params.row?.[type]?._id} object_type={type} {...(params.row?.[type]?.attachments?.at(-1) ?? {})} />

          <Stack>
            <Box sx={{ position: "relative", lineHeight: 1.43, width: 'max-content' }}>
              <Typography
                title="Click to open in new tab"
                component={"a"}
                target="_blank"
                href={type === "bom" ? AppRoutes.viewBomBasicDetail(params.row._id) : AppRoutes.viewMatBasicDetail(params.row._id)}
                variant="body2"
              >
                {type === "bom" ? params.row.bom.sequence_id : params.row.material.sequence_id}{`-r${params.row.version}`} <OpenInNewOutlined sx={{ fontSize: "12px", marginBottom: "-2px" }} fontSize="small" />
              </Typography>
              <Tooltip title={params?.row?.[type]?.released ? "Published" : "Un Published"} >
                <Stack
                  id={`${type === "bom" ? "bom" : "material"}-publish-success`}
                  sx={{
                    position: "absolute",
                    top: -2,
                    right: -25,
                    height: 24,
                  }}>
                  {params?.row?.[type]?.released
                    ? <PublishedIcon sx={{ width: 24, height: 24 }} />
                    : <WIPIcon sx={{ width: 24, height: 24 }} />}
                </Stack>
              </Tooltip>
            </Box>

            <Typography variant="subtitle2" color="textSecondary">
              {params.row?.[type]?.short_description ?? ""}
            </Typography>
          </Stack>

        </Stack>
      }
    },
    {
      field: "serial_num",
      headerName: "Tag No.",
      valueGetter: (value, row, column, apiRef) => row.serial_num,
      editable: false,
    },
    // {
    //   field: "sequence_id",
    //   headerName: type === "bom" ? "BOM ID" : "Material ID",
    //   width: 125,
    //   renderCell: (params: GridRenderCellParams) => {
    //     return (
    //       <Link
    //         title="Click to open in new tab"
    //         target="_blank"
    //         to={{
    //           pathname: type === "bom" ? AppRoutes.viewBomBasicDetail(params.row._id) : AppRoutes.viewMatBasicDetail(params.row._id),
    //         }}
    //         state={paths}
    //       >
    //         {type === "bom" ? params.row.bom.sequence_id : params.row.material.sequence_id} <OpenInNewOutlined sx={{ fontSize: "12px", marginBottom: "-2px" }} fontSize="small" />
    //       </Link>
    //     );
    //   }
    // },
    // {
    //   field: "version",
    //   headerClassName: "bomHeader",
    //   headerName: "Revision",
    // },
    // {
    //   field: "short_description",
    //   width: 320,
    //   headerClassName: "bomHeader",
    //   headerName: "Short Description",
    //   valueGetter: (value, row, column, apiRef) => row?.[type]?.short_description ?? ""
    // },
    // {
    //   field: "attachments",
    //   headerName: "Image",
    //   width: 70,
    //   disableColumnMenu: true,
    //   sortable: false,
    //   renderCell: (params: GridRenderCellParams) => <AttachmentImage object_id={params.row?.[type]?._id} object_type={type} {...(params.row?.[type]?.attachments?.at(-1) ?? {})} />,
    // },
    // {
    //   field: 'released',
    //   headerName: 'Status',
    //   width: 70,
    //   disableColumnMenu: true,
    //   sortable: false,
    //   renderCell: (params: GridRenderCellParams) => {
    //     return (
    //       (<Stack
    //         id={`${type === "bom" ? "bom" : "material"}-publish-success`}
    //         sx={{
    //           py: 2,
    //           pt: "36px"
    //         }}>
    //         {params?.row?.[type]?.released
    //           ? <Tooltip title="Published" children={<div><PublishedIcon /></div>} />
    //           : <Tooltip title="Un Published" children={<div><WIPIcon /></div>} />}
    //       </Stack>)
    //     );
    //   }
    // },
    {
      field: "group_name",
      headerClassName: "bomHeader",
      headerName: "Group",
      valueGetter: (value, row, column, apiRef) => row?.[type]?.group_name ?? ""
    },
    {
      field: "noun_name",
      headerClassName: "bomHeader",
      headerName: "Noun",
      valueGetter: (value, row, column, apiRef) => row?.[type]?.noun_name ?? ""
    },
    {
      field: "reference_id",
      width: 150,
      headerClassName: "bomHeader",
      headerName: "Ref Number",
      valueGetter: (value, row, column, apiRef) => type === 'bom' ? row?.[type]?.reference_id : row?.[type]?.external_ref_id ?? ""
    },
    {
      field: "symbol_assigned",
      width: 150,
      headerClassName: "bomHeader",
      headerName: "Symbol assigned",
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box>
            {params.row.symbol_assigned ? params.row.symbol_id : 'No'}
          </Box>
        )
      },
    },
    {
      field: "original_cost",
      headerClassName: "bomHeader",
      headerName: "Cost",
      valueGetter: (value, row, column, apiRef) => `${row?.material?.money?.cost ?? "0"} ${row?.material?.money?.currency ?? defaultCurrency}`,
    },
    {
      field: "spare_part",
      type: "singleSelect",
      valueOptions: (params) => ["Yes", "No"],
      valueGetter: (value, row, column, apiRef) =>
        [true, "Yes"].includes(row.spare_part) ? "Yes" : "No",
      headerName: "Spare Part",
      editable: isEditable,
    },
    {
      field: "spare_part_category",
      width: 180,
      type: "singleSelect",
      headerName: "Spare Part Category",
      editable: isEditable,
      description: sparePartsData.isLoading ? "Fetching Spare Parts" : "Spare Parts",
      valueOptions: (params) => sparePartsData.data?.spareParts ?? ["None"],
      valueGetter: (value, row, column, apiRef) => {
        return row?.spare_part_category || null;
      },
      renderEditCell: (params) =>
        enableCategoryBasedOnSparePart(params, sparePartsData),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = ![true, "Yes"].includes(params.otherFieldsProps?.spare_part?.value);
        return { ...params.props, value: hasError ? null : params?.props?.value };
      },
    },
    {
      field: "spare_part_quantity",
      type: "number",
      width: 125,
      editable: isEditable,
      headerName: "Spare Part Qty",
      renderEditCell: enableSpareQtyBasedOnSparePart,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = ![true, "Yes"].includes(params.otherFieldsProps?.spare_part?.value);
        return { ...params.props, value: hasError ? 0 : params?.props?.value };
      },
    },
    {
      field: "normal_quantity",
      type: "number",
      editable: isEditable,
      headerName: "Qty",
      valueGetter: (value, row, column, apiRef) => row?.normal_quantity,
    },
    {
      field: "override_cost",
      type: "number",
      width: 180,
      editable: isEditable,
      headerName: "Override cost per qty",
      valueGetter: (value, row, column, apiRef) => row?.override_cost || '',
    },
    {
      field: "currency",
      type: "number",
      width: 125,
      headerName: "Currency",
      valueGetter: (value, row, column, apiRef) => {
        if (type === 'material') {
          return row?.[type]?.money?.currency ?? defaultCurrency
        } else {
          return row?.[type]?.currency_code ?? defaultCurrency
        }
      },
    },
    {
      field: "child_status",
      width: 180,
      type: "singleSelect",
      headerName: type === "bom" ? "Child BOM Status" : "Child Material Status",
      editable: isEditable,
      description: bomStatusData.isLoading || materialStatusData.isLoading
        ? "Fetching Status"
        : "Status",
      valueOptions: (params) => type === "bom" ? bomStatusData.data?.childStatus ?? ["None"] : materialStatusData.data?.childStatus ?? ["None"],
      valueGetter: (value, row, column, apiRef) => {
        return row?.child_status || null;
      },
    }
  ];

  if (type === "bom") {
    let index = rows.findIndex((r: GridColDef) => r.field === "original_cost");
    rows = [...rows.slice(0, index), ...rows.slice(index + 1)];
  }

  if (type === "material") {
    let index = rows.findIndex((r: GridColDef) => r.field === "original_cost");
    rows = [...rows.slice(0, index), rows[index], {
      field: "uom_type",
      headerClassName: "bomHeader",
      headerName: "UOM",
      valueGetter: (value, row, column, apiRef) => row.uom_type,
    },
    {
      field: "uom_value",
      headerClassName: "bomHeader",
      width: 140,
      headerName: "Material Value",
      valueGetter: (value, row, column, apiRef) => `${row.metric_type?.value ?? ""}`,
    },
    {
      field: "uom_unit",
      headerClassName: "bomHeader",
      headerName: "Unit",
      valueGetter: (value, row, column, apiRef) => `${row.metric_type?.unit ?? ""}`,
    },
    {
      field: "required_dim_uom",
      headerClassName: "bomHeader",
      type: "number",
      width: 140,
      renderHeader: (params) => {
        return (
          <Tooltip arrow title="Enter the actual value in the same units as the Material value" placement="top">
            <Stack
              direction={"row"}
              sx={{
                gap: 1,
                alignItems: "center"
              }}>
              <Typography variant="body2" ><b>{"BOM Value"}</b></Typography>
              <Info color="action" fontSize='small' />
            </Stack>
          </Tooltip>
        );
      },
      editable: isEditable,
    },
    {
      field: "lead_time_days",
      headerClassName: "bomHeader",
      headerName: "Lead Time (Weeks)",
      width: 180,
      valueGetter: (value, row, column, apiRef) => row.lead_time_days,
    }, ...rows.slice(index + 1)];
  }

  if (!isEditable) {
    rows.shift();
    rows = [...rows.slice(0, 1), ...rows.slice(2, 6)];
  }

  rows.push({
    field: "optional_info",
    headerClassName: "bomHeader",
    headerName: "Remarks",
    editable: isEditable,
  })

  return rows;
};